import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaMoneyBillWave,
  FaEdit,
  FaTrash,
  FaPlus,
  FaFilter,
  FaLayerGroup,
  FaSearch,
} from "react-icons/fa";
import { toast } from "react-toastify";
import FeeStructureForm from "../forms/FeeStructureForm";
import { server } from "../../server";
import useAuth from "../../useAuth";

export default function FeeStructureList() {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const { token } = useAuth();

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/fee-structures`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let fetchedItems = res.data.data || [];
      if (classFilter) {
        fetchedItems = fetchedItems.filter(
          (item) => String(item.class) === String(classFilter)
        );
      }
      setItems(fetchedItems);
    } catch (error) {
      toast.error("Failed to load fee structures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchItems();
  }, [classFilter, token]);

  const handleDelete = async (id) => {
    if (window.confirm("Delete this fee structure?")) {
      try {
        await axios.delete(`${server}/fee-structures/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Fee structure deleted successfully.");
        fetchItems();
      } catch (error) {
        toast.error("Error deleting fee structure");
      }
    }
  };

  const filteredItems = items.filter(
    (item) =>
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.class?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaMoneyBillWave size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Fee Structures Engine</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Configure foundational class-wise fee structures and installment amounts</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedItem(null);
              setIsModalOpen(true);
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Add Fee Rule
          </button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Total Fee Rules</span>
          <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">{items.length}</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Filtered Class</span>
          <div className="text-sm font-black text-emerald-600 dark:text-emerald-400 mt-1">
            {classFilter ? `Class ${classFilter}` : "All Classes"}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Active Rules</span>
          <div className="text-xl font-black text-gray-900 dark:text-white mt-0.5">
            {items.filter((i) => i.isActive !== false).length}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Academic Year</span>
          <div className="text-sm font-black text-gray-900 dark:text-white mt-0.5">2026 - 2027</div>
        </div>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="relative w-full sm:w-80">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
            placeholder="Search Title or Class..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <FaFilter className="text-gray-400 text-xs" />
          <select
            className="px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
          >
            <option value="">All Classes</option>
            {["UKG", "LKG", "Nursery", "Pre", ...Array.from({ length: 12 }, (_, i) => String(i + 1))].map((c) => (
              <option key={c} value={c}>
                Class {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* High-Density Table Display */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">Class</th>
                <th className="p-2.5">Structure Title</th>
                <th className="p-2.5">Frequency</th>
                <th className="p-2.5">Due Date</th>
                <th className="p-2.5 text-right">Amount</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-indigo-600 font-bold">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading fee structures...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-400 italic">
                    No matching fee structure rules found.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition">
                    <td className="p-2.5 font-black text-indigo-600 dark:text-indigo-400">Class {item.class}</td>
                    <td className="p-2.5 font-black text-gray-900 dark:text-white">{item.title}</td>
                    <td className="p-2.5 font-bold">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-md text-[10px]">
                        {item.frequency || "Monthly"}
                      </span>
                    </td>
                    <td className="p-2.5 text-gray-500 font-medium whitespace-nowrap">
                      {new Date(item.dueDate).toLocaleDateString()}
                    </td>
                    <td className="p-2.5 text-right font-black text-emerald-600 dark:text-emerald-400">
                      ₹{(item.amount || 0).toLocaleString()}
                    </td>
                    <td className="p-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => {
                            setSelectedItem(item);
                            setIsModalOpen(true);
                          }}
                          className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-lg hover:bg-indigo-100 transition"
                          title="Edit Structure"
                        >
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 transition"
                          title="Delete Structure"
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      <FeeStructureForm
        structure={selectedItem}
        itemToEdit={selectedItem}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchItems}
      />
    </div>
  );
}
